'use client';

import { useEffect, useRef, useState } from 'react';

export function useReveal<T extends HTMLElement = HTMLDivElement>(options?: IntersectionObserverInit) {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Fallback: reveal after 2.5s no matter what (covers full-page screenshots)
    const fallbackTimer = setTimeout(() => setVisible(true), 2500);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
          clearTimeout(fallbackTimer);
        }
      },
      {
        // Reveal early - when element is within 300px of entering viewport
        threshold: 0.05,
        rootMargin: '0px 0px 300px 0px',
        ...options,
      }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, [options]);

  return { ref, visible };
}
